import { xfetch } from '../../../lib/xfetch';
import type { LastPageButtonProps, Question } from '../../../survey';
import { getApiBase, REQUEST_TIMEOUT } from '../../config';
import { ResponseSuccessFieldError, type BaseErrorResponseData, type BaseSuccessResponseData } from '../../types';
import { ErrorCode } from './constants';
import type { ThemeId } from '../../../app/themes/themes';

import {
  EpkCmPairError,
  IncorrectDataError,
  OtherError,
  SurveyAlreadyCompletedError,
  SurveyFormFetchingError,
  SurveyNotFoundError,
  TechnicalError,
  UnknownServerMessageError,
  UserExcludedByPolicyError,
  UserNotConfirmedError,
  UserNotFoundError
} from './error-types';

type SurveyForm = {
  id: number; // form version id

  /**
   * Количество вопросов для заполнения прогресс-бара до 98%.
   * Количество вопросов в самой длинной ветке анкеты.
   * Рубильник для прогресс-бара.
   */
  bar_max_size?: number;

  end_point_name?: string;
  end_point_url?: string;
  button_name?: string;
  formQuestions: Question[];
  clientInfo: {
    design: ThemeId;
  };
};

type GetFormResponseData =
  | (BaseSuccessResponseData & {
      body:
        | {
            success: true;
            data: SurveyForm;
          }
        | {
            success: false;
            message: string;
            code: ErrorCode;
          };
    })
  | BaseErrorResponseData;

type FetchSurveyFormResult = {
  formVersionId: SurveyForm['id'];
  questions: SurveyForm['formQuestions'];
  maxProgressStepsCount?: SurveyForm['bar_max_size'];
  defaultLastPageButtonProps: LastPageButtonProps;
  design: SurveyForm['clientInfo']['design'];
};

export const fetchSurveyForm = async (cmId: string): Promise<FetchSurveyFormResult> => {
  try {
    const data = await xfetch<GetFormResponseData>(`${getApiBase()}/getForm/${cmId}`, { timeout: REQUEST_TIMEOUT });

    if (!data.success) {
      throw new ResponseSuccessFieldError('data.success');
    }

    if (!data.body.success) {
      const { code, message } = data.body;

      switch (code) {
        case ErrorCode.INCORRECT_DATA:
          throw new IncorrectDataError(message);
        case ErrorCode.USER_NOT_CONFIRMED:
          throw new UserNotConfirmedError(message);
        case ErrorCode.SURVEY_ALREADY_COMPLETED:
          throw new SurveyAlreadyCompletedError(message);
        case ErrorCode.USER_EXCLUDED_BY_POLICY:
          throw new UserExcludedByPolicyError(message);
        case ErrorCode.SURVEY_NOT_FOUND:
          throw new SurveyNotFoundError(message);
        case ErrorCode.USER_NOT_FOUND:
          throw new UserNotFoundError(message);
        case ErrorCode.TECHNICAL_ERROR:
          throw new TechnicalError(message);
        case ErrorCode.EPK_CM_PAIR:
          throw new EpkCmPairError(message);
        case ErrorCode.OTHER:
          throw new OtherError(message);
        default:
          void 0;
      }

      if (message) {
        throw new UnknownServerMessageError(message);
      }

      throw new ResponseSuccessFieldError('data.body.success');
    }

    const { id, formQuestions, bar_max_size, button_name, end_point_url, clientInfo } = data.body.data;

    return {
      formVersionId: id,
      questions: formQuestions,
      maxProgressStepsCount: bar_max_size,
      defaultLastPageButtonProps: {
        text: button_name,
        url: end_point_url
      },
      design: clientInfo.design
    };
  } catch (err) {
    if (err instanceof SurveyFormFetchingError) {
      throw err;
    }

    throw new SurveyFormFetchingError(undefined, { cause: err });
  }
};
